import React from 'react';

const DashboardStats = ({ stats, onSearchClick }) => {
  const styles = {
    container: {
      padding: '2rem 0'
    },
    header: {
      marginBottom: '3rem'
    },
    welcomeTitle: {
      fontSize: '2.5rem',
      fontWeight: '800',
      background: 'linear-gradient(135deg, #FFFFFF 0%, #B0C4DE 100%)',
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
      marginBottom: '1rem'
    },
    subtitle: {
      fontSize: '1.2rem',
      color: '#B0C4DE',
      marginBottom: '2rem'
    },
    accountStatus: {
      backgroundColor: 'rgba(13, 27, 49, 0.85)',
      border: '1px solid rgba(255, 255, 255, 0.1)',
      borderRadius: '16px',
      padding: '1.5rem',
      marginBottom: '2rem'
    },
    statusText: {
      fontSize: '1.1rem',
      color: '#FFFFFF',
      marginBottom: '0.5rem'
    },
    planInfo: {
      fontSize: '0.9rem',
      color: '#B0C4DE'
    },
    statsGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
      gap: '1.5rem',
      marginBottom: '3rem'
    },
    statCard: {
      backgroundColor: 'rgba(13, 27, 49, 0.85)',
      border: '1px solid rgba(255, 255, 255, 0.1)',
      borderRadius: '16px',
      padding: '2rem',
      backdropFilter: 'blur(20px)',
      transition: 'all 0.3s ease',
      cursor: 'pointer'
    },
    statIcon: {
      width: '48px',
      height: '48px',
      backgroundColor: 'rgba(41, 121, 255, 0.2)',
      borderRadius: '12px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      marginBottom: '1rem'
    },
    statValue: {
      fontSize: '2.5rem',
      fontWeight: '800',
      color: '#FFFFFF',
      marginBottom: '0.5rem'
    },
    statLabel: {
      fontSize: '0.9rem',
      color: '#B0C4DE',
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
      fontWeight: '600'
    },
    ctaSection: {
      textAlign: 'center',
      padding: '3rem 0'
    },
    ctaButton: {
      backgroundColor: '#2979FF',
      color: '#FFFFFF',
      border: 'none',
      borderRadius: '12px',
      padding: '1rem 2rem',
      fontSize: '1.1rem',
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      boxShadow: '0 8px 25px rgba(41, 121, 255, 0.4)'
    },
    lastRefresh: {
      fontSize: '0.8rem',
      color: '#7F8C9A',
      textAlign: 'center',
      marginTop: '2rem'
    }
  };

  const formatNumber = (num) => {
    if (num >= 1000000) {
      return (num / 1000000).toFixed(1) + 'M';
    } else if (num >= 1000) {
      return (num / 1000).toFixed(1) + 'K';
    }
    return num?.toLocaleString() || '0';
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div style={styles.container}>
      {/* Welcome Header */}
      <div style={styles.header}>
        <h1 style={styles.welcomeTitle}>
          Welcome, {stats?.user?.name || 'Sales Manager'}!
        </h1>
        <p style={styles.subtitle}>
          AI-powered solar installer intelligence at your fingertips
        </p>
        
        {/* Account Status */}
        <div style={styles.accountStatus}>
          <div style={styles.statusText}>
            Your '{stats?.user?.plan || 'Free'}' plan is active. 
            You have used {stats?.user?.exportsUsed || 0} of your {stats?.user?.exportsLimit || 10} monthly lead exports.
          </div>
          <div style={styles.planInfo}>
            {stats?.user?.company && `Company: ${stats.user.company}`}
          </div>
        </div>
      </div>

      {/* Key Statistics */}
      <div style={styles.statsGrid}>
        <div 
          style={styles.statCard}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-8px)';
            e.currentTarget.style.boxShadow = '0 25px 50px rgba(0, 0, 0, 0.4)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = 'none';
          }}
        >
          <div style={styles.statIcon}>
            <svg width="24" height="24" fill="none" stroke="#2979FF" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
              <circle cx="9" cy="7" r="4"/>
              <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
              <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
            </svg>
          </div>
          <div style={styles.statValue}>
            {formatNumber(stats?.leads?.totalLeads || stats?.stats?.totalInstallers)}
          </div>
          <div style={styles.statLabel}>
            {stats?.leads?.totalLeads ? 'Total Leads Available' : 'Total Installers in Database'}
          </div>
        </div>

        <div 
          style={styles.statCard}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-8px)';
            e.currentTarget.style.boxShadow = '0 25px 50px rgba(0, 0, 0, 0.4)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = 'none';
          }}
        >
          <div style={styles.statIcon}>
            <svg width="24" height="24" fill="none" stroke="#00E676" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
            </svg>
          </div>
          <div style={styles.statValue}>
            {formatNumber(stats?.leads?.activeInstallers || stats?.stats?.newThisWeek)}
          </div>
          <div style={styles.statLabel}>
            {stats?.leads?.activeInstallers ? 'Active Installers' : 'New Installers Added This Week'}
          </div>
        </div>

        <div 
          style={styles.statCard}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-8px)';
            e.currentTarget.style.boxShadow = '0 25px 50px rgba(0, 0, 0, 0.4)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = 'none';
          }}
        >
          <div style={styles.statIcon}>
            <svg width="24" height="24" fill="none" stroke="#FF9100" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"/>
            </svg>
          </div>
          <div style={styles.statValue}>
            {formatNumber(stats?.leads?.recentActivity || stats?.stats?.activeLastMonth)}
          </div>
          <div style={styles.statLabel}>
            {stats?.leads?.recentActivity ? 'Recent Activity (30 Days)' : 'Active Installers (Last 30 Days)'}
          </div>
        </div>

        <div 
          style={styles.statCard}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-8px)';
            e.currentTarget.style.boxShadow = '0 25px 50px rgba(0, 0, 0, 0.4)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = 'none';
          }}
        >
          <div style={styles.statIcon}>
            <svg width="24" height="24" fill="none" stroke="#9C27B0" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
            </svg>
          </div>
          <div style={styles.statValue}>
            {stats?.user?.exportsLimit - stats?.user?.exportsUsed || 0}
          </div>
          <div style={styles.statLabel}>
            Remaining Exports This Month
          </div>
        </div>
      </div>

      {/* Call to Action */}
      <div style={styles.ctaSection}>
        <button 
          style={styles.ctaButton}
          onClick={onSearchClick}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-3px)';
            e.currentTarget.style.boxShadow = '0 15px 35px rgba(41, 121, 255, 0.6)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 8px 25px rgba(41, 121, 255, 0.4)';
          }}
        >
          {stats?.leads?.totalLeads ? '🔍 Browse Leads' : '🔍 Search Installers'}
        </button>
      </div>

      {/* Last Refresh */}
      <div style={styles.lastRefresh}>
        Data Last Refreshed: {formatDate(stats?.stats?.lastDataRefresh)}
      </div>
    </div>
  );
};

export default DashboardStats; 